// ---------------------------------------------------------------------------
// SyntheticRunPreview — Shared right-panel content for a synthetic run.
//
// Polls the run, resolves the appropriate renderer, fetches required blobs,
// renders Summary + Items, and exposes a "Save dataset" action that writes
// the renderer-sliced entries to the Data Hub.
//
// Used by:
//   - SyntheticJobPreviewPage (legacy standalone route)
//   - NewSyntheticDatasetPage (full-page wizard shell)
//
// Caller must wrap in DataHubClientProvider — we read the data-hub client
// here for the Save flow.
// ---------------------------------------------------------------------------

import {
  Badge,
  Body1,
  Button,
  Spinner,
  Toast,
  ToastBody,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from '@fluentui/react-components';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { AppError, isAccessDeniedError } from '../../../api/errors.js';
import { useHttpClient } from '../../../api/HttpClientContext.js';
import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import { useRunBlobsText } from '../../../hooks/useRunBlobsText/useRunBlobsText.js';
import { useSyntheticJob } from '../../../hooks/useSyntheticJob/useSyntheticJob.js';
import type {
  DataEntry,
  Dataset,
} from '../../../services/dataHubService.interface.js';
import { DataHubService } from '../../../services/dataHubService.js';
import type { SyntheticJobStatus } from '../../../services/syntheticJob.interface.js';
import { WorkflowApiService } from '../../../services/workflowApi.js';
import { runConcurrent } from '../../../utils/concurrency.js';
import { DatalakeAccessDenied } from '../../Datalake/DatalakeAccessDenied/DatalakeAccessDenied.js';
import {
  CONCURRENT_UPLOADS,
  uploadEntryFiles,
} from '../../Datalake/UploadFolderDialog/datasetUploadUtils.js';
import { getSyntheticRenderer } from '../renderers/registry.js';
import type {
  SyntheticDataEntry,
  SyntheticEntryFile,
  SyntheticRendererContext,
  SyntheticResultRenderer,
} from '../renderers/renderer.interface.js';
import type { SaveSyntheticDatasetFormValues } from '../SaveSyntheticDatasetDialog/SaveSyntheticDatasetDialog.interface.js';
import { SaveSyntheticDatasetDialog } from '../SaveSyntheticDatasetDialog/SaveSyntheticDatasetDialog.js';

import type { SyntheticRunPreviewProps } from './SyntheticRunPreview.interface.js';
import { useStyles } from './SyntheticRunPreview.style.js';

export type {
  SyntheticRunPreviewHandle,
  SyntheticRunPreviewProps,
} from './SyntheticRunPreview.interface.js';

export function SyntheticRunPreview({
  jobId,
  defaultDatasetName,
  controlsRef,
  onCanSaveChange,
}: SyntheticRunPreviewProps) {
  const styles = useStyles();
  const navigate = useNavigate();

  const dataHubClient = useDataHubClient();
  const dataHubService = useMemo(
    () => new DataHubService(dataHubClient),
    [dataHubClient],
  );
  const httpClient = useHttpClient();
  const workflowApi = useMemo(
    () => new WorkflowApiService(httpClient),
    [httpClient],
  );
  const toasterId = useId('synthetic-preview-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const { job, isLoading, error, isTerminal } = useSyntheticJob(jobId);
  const results = job?.runState === 'completed' ? (job.result ?? null) : null;

  const renderer = useMemo(
    () => (job ? getSyntheticRenderer(job.generationType) : null),
    [job],
  );

  const blobNamesToLoad = useMemo<string[]>(() => {
    if (!renderer || !results) return [];
    return renderer.selectBlobsToLoad
      ? renderer.selectBlobsToLoad(results)
      : (results.blobs ?? []).filter((n) => n.toLowerCase().endsWith('.json'));
  }, [renderer, results]);

  const { blobs: loadedBlobs } = useRunBlobsText(
    jobId,
    blobNamesToLoad,
    results !== null,
  );

  const rendererCtx = useMemo(() => {
    if (!job || !results) return null;
    return { run: job, result: results, blobs: loadedBlobs };
  }, [job, results, loadedBlobs]);

  const [saveOpen, setSaveOpen] = useState(false);

  const canSave = renderer !== null && rendererCtx !== null;

  useImperativeHandle(
    controlsRef,
    () => ({
      openSaveDialog: () => {
        if (canSave) setSaveOpen(true);
      },
    }),
    [canSave],
  );

  useEffect(() => {
    onCanSaveChange?.(canSave);
  }, [canSave, onCanSaveChange]);

  const handleSave = useCallback(
    async (values: SaveSyntheticDatasetFormValues) => {
      if (!renderer || !rendererCtx) {
        throw new Error('No result available to save.');
      }
      let entries;
      try {
        entries = renderer.toDataEntries(rendererCtx);
      } catch (err) {
        showErrorToast(dispatchToast, 'Result not ready', err);
        throw err;
      }
      if (entries.length === 0) {
        throw new Error('No result files available to save.');
      }

      // Stamp every entry with lineage back to the synthetic run, so a saved
      // dataset can be traced to (and one day rehydrated from) its origin.
      const lineageProps = {
        syntheticRunId: jobId,
        generationType: rendererCtx.run.generationType,
        schemaVersion: 1,
      };
      entries = entries.map((e) => ({
        ...e,
        properties: { ...lineageProps, ...(e.properties ?? {}) },
      }));

      // DataLake rejects entries whose type differs from the dataset's.
      const datasetDataType = renderer.datasetDataType ?? values.dataType;

      // Persist the generation metadata as its own entry so the parameters
      // that produced this dataset live alongside the data itself. Prepended
      // and given a reserved `__synthetic_metadata__` name so it's clearly
      // not user data. Followup PR will move this to dataset-level lineage
      // (tags / dataset properties) once the DataHub API supports it.
      const metadataBlobName = (rendererCtx.result.blobs ?? []).find(
        (n) => n.toLowerCase() === 'metadata.json',
      );
      const metadataText = metadataBlobName
        ? rendererCtx.blobs[metadataBlobName]
        : undefined;
      const unfetchedFiles: string[] = [];
      if (metadataText !== undefined) {
        let metadata: unknown;
        try {
          metadata = JSON.parse(metadataText);
        } catch {
          // Preserve the data even if it fails to parse, so nothing is lost.
          metadata = { raw: metadataText };
        }
        const extraFiles = await fetchMetadataBlobs(
          workflowApi,
          jobId,
          renderer.metadataBlobs?.(rendererCtx.result) ?? [],
          unfetchedFiles,
        );
        const metadataEntry: SyntheticDataEntry = {
          dataType: datasetDataType,
          name: METADATA_ENTRY_NAME,
          description: 'Synthetic generation parameters and metadata.',
          properties: {
            ...lineageProps,
            kind: 'syntheticMetadata',
            metadata,
          },
          files: [
            {
              fileName: metadataBlobName ?? 'metadata.json',
              content: metadataText,
            },
            ...extraFiles,
          ],
        };
        entries = [metadataEntry, ...entries];
      }

      let dataset: Dataset;
      try {
        dataset = await dataHubService.createDataset({
          name: values.name,
          description: `Synthetic generation run ${jobId}`,
          accessCategory: values.accessCategory,
          dataType: datasetDataType,
          sensitivityLabel: values.sensitivityLabel,
        });
      } catch (err) {
        showErrorToast(
          dispatchToast,
          'Failed to create dataset',
          err,
          isAccessDeniedError(err) ? (
            <DatalakeAccessDenied action="create datasets" hideTitle />
          ) : undefined,
        );
        throw err;
      }

      try {
        const result = await dataHubService.batchAddDataEntries(
          dataset.id,
          entries,
        );
        const { uploaded, failedFiles } = await uploadSyntheticEntryFiles(
          dataHubService,
          dataset.id,
          entries,
          result.succeeded,
        );
        failedFiles.unshift(...unfetchedFiles);
        const succeeded = result.succeeded.length;
        const failed = result.failed.length;
        const hasFailures = failed > 0 || failedFiles.length > 0;
        dispatchToast(
          <Toast>
            <ToastTitle>Dataset saved</ToastTitle>
            <ToastBody>
              {succeeded} item{succeeded === 1 ? '' : 's'} added
              {failed > 0 ? `, ${failed} failed` : ''}; {uploaded} file
              {uploaded === 1 ? '' : 's'} uploaded
              {failedFiles.length > 0
                ? `, ${failedFiles.length} failed (${failedFiles.join(', ')})`
                : ''}
              .
            </ToastBody>
          </Toast>,
          { intent: hasFailures ? 'warning' : 'success', timeout: 5000 },
        );
      } catch (err) {
        showErrorToast(
          dispatchToast,
          'Failed to add data entries',
          err,
          isAccessDeniedError(err) ? (
            <DatalakeAccessDenied action="add data to datasets" hideTitle />
          ) : undefined,
        );
        throw err;
      }

      await navigate(`/datasets?datasetId=${encodeURIComponent(dataset.id)}`);
    },
    [
      renderer,
      rendererCtx,
      dataHubService,
      workflowApi,
      dispatchToast,
      navigate,
      jobId,
    ],
  );

  const itemCount = useMemo(
    () =>
      rendererCtx !== null
        ? safeItemCount(renderer, rendererCtx)
        : blobNamesToLoad.length,
    [renderer, rendererCtx, blobNamesToLoad],
  );

  return (
    <div className={styles.root}>
      <Toaster toasterId={toasterId} position="top-end" />

      {renderStatusRow(
        styles,
        isLoading,
        error,
        isTerminal,
        job?.runState,
        job?.error,
      )}

      {renderer && rendererCtx && (
        <section className={styles.resultSection}>
          <renderer.Summary {...rendererCtx} />
          <renderer.Items {...rendererCtx} />

          {!controlsRef && !onCanSaveChange && (
            <div className={styles.actions}>
              <Button
                type="button"
                appearance="primary"
                onClick={() => setSaveOpen(true)}
              >
                Save dataset
              </Button>
            </div>
          )}
        </section>
      )}

      <SaveSyntheticDatasetDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        defaultName={defaultDatasetName}
        itemCount={itemCount}
        onSave={handleSave}
      />
    </div>
  );
}

type Styles = ReturnType<typeof useStyles>;

function renderStatusRow(
  styles: Styles,
  isLoading: boolean,
  error: string | null,
  isTerminal: boolean,
  runState: SyntheticJobStatus | undefined,
  runError: string | undefined,
): React.ReactNode {
  if (isLoading && runState === undefined) {
    return (
      <div className={styles.statusRow}>
        <Spinner size="small" label="Loading job status..." />
      </div>
    );
  }
  if (error !== null && runState === undefined) {
    return <Body1 className={styles.errorBanner}>{error}</Body1>;
  }
  if (runState === undefined) return null;

  return (
    <div className={styles.statusRow}>
      <StatusBadge status={runState} />
      {!isTerminal && <Spinner size="extra-small" />}
      {runState === 'failed' && runError && (
        <Body1 className={styles.errorBanner}>{runError}</Body1>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: SyntheticJobStatus }) {
  const color =
    status === 'completed'
      ? 'success'
      : status === 'failed'
        ? 'danger'
        : status === 'running'
          ? 'informative'
          : 'subtle';
  return (
    <Badge appearance="filled" color={color}>
      {status}
    </Badge>
  );
}

function safeItemCount(
  renderer: SyntheticResultRenderer | null,
  ctx: SyntheticRendererContext,
): number {
  if (!renderer) return 0;
  try {
    return renderer.toDataEntries(ctx).length;
  } catch {
    return 0;
  }
}

const METADATA_ENTRY_NAME = '__synthetic_metadata__';

// Fetches metadata-only result blobs at save time; names that fail to load
// are recorded in `failed` and skipped so the save still goes ahead.
async function fetchMetadataBlobs(
  workflowApi: WorkflowApiService,
  runId: string,
  blobNames: string[],
  failed: string[],
): Promise<SyntheticEntryFile[]> {
  const results = await Promise.allSettled(
    blobNames.map((name) => workflowApi.getSyntheticRunBlobText(runId, name)),
  );
  const files: SyntheticEntryFile[] = [];
  results.forEach((r, i) => {
    const fileName = blobNames[i]!;
    if (r.status === 'fulfilled') files.push({ fileName, content: r.value });
    else failed.push(`${METADATA_ENTRY_NAME}/${fileName}`);
  });
  return files;
}

// Batch-create cannot carry files, so each created entry's files are uploaded
// separately, matched back to their request by entry name.
async function uploadSyntheticEntryFiles(
  service: DataHubService,
  dataSetId: string,
  requested: SyntheticDataEntry[],
  created: DataEntry[],
): Promise<{ uploaded: number; failedFiles: string[] }> {
  const filesByName = new Map(requested.map((e) => [e.name, e.files ?? []]));
  let uploaded = 0;
  const failedFiles: string[] = [];

  await runConcurrent(created, CONCURRENT_UPLOADS, async (entry) => {
    const entryName = entry.name ?? '';
    const files = filesByName.get(entryName) ?? [];
    if (files.length === 0) return;
    const subTypes = new Map<string, string>();
    for (const f of files) {
      if (f.subType) subTypes.set(f.fileName, f.subType);
    }
    const entryFailed: string[] = [];
    let entryUploaded = 0;
    try {
      await uploadEntryFiles(
        service,
        dataSetId,
        entry.dataId,
        files.map((f) => new File([f.content], f.fileName)),
        undefined,
        () => {
          entryUploaded++;
        },
        (fileName) => {
          entryFailed.push(`${entryName}/${fileName}`);
        },
        undefined,
        subTypes,
      );
      uploaded += entryUploaded;
      failedFiles.push(...entryFailed);
    } catch {
      // Completion failed, so none of this entry's files were registered.
      failedFiles.push(...files.map((f) => `${entryName}/${f.fileName}`));
    }
  });

  return { uploaded, failedFiles };
}

function showErrorToast(
  dispatchToast: ReturnType<typeof useToastController>['dispatchToast'],
  title: string,
  err: unknown,
  body?: ReactNode,
) {
  const message =
    err instanceof AppError
      ? err.message
      : err instanceof Error
        ? err.message
        : 'Unexpected error';
  dispatchToast(
    <Toast>
      <ToastTitle>{title}</ToastTitle>
      <ToastBody>{body ?? message}</ToastBody>
    </Toast>,
    { intent: 'error', timeout: 6000 },
  );
}
